import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, FormHelperText, Grid, Modal } from "@mui/material";
import { CTextFieldIdentifyName } from "@/components/TextField/CTextFieldIdentifyName";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CTextFieldPastDays } from "@/components/TextField/CTextFieldPastDays";
import { CSelect } from "@/components/Select/CSelect";
import { CTextField } from "@/components/TextField/CTextField";
import { CCheckbox } from "@/components/Checkbox/CCheckbox";
import {
  getPrefectureIdByValue,
  getPrefectureSelectObject,
  getPrefectureNameById,
  getCitySelectObject,
  getRealEstateTypeEngSelectObject,
  transformChibanKaokuNumber,
  getInitialPictureRequestOptions,
  createAcquireMultipleBookHandoffData,
  checkKaokuNumberSearchDisabled,
  pastDaysDescription,
  convertBookTypeToJpn,
} from "@/utils/utilsAcquireBook";
import { Button, ButtonVariantOption } from "@/components/Button";
import { type AcquireBookForm } from "@/features/acquireBook/hooks/useAcquireBookForm";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCityWithExternalState } from "@/hooks/useCity";
import { createAcquireBookPageFormSchema } from "@/schemas/acquirebook";
import { ChibanKaokuNumberSearchModal } from "@/components/KaokuNumberSearch/ChibanKaokuNumberSearchModal";
import { type PropertySelectionRow } from "@/features/map/types";
import {
  type AcquireBookPageFormData,
  type AcquireMultipleBookLocationState,
  type BookTypeEng,
} from "@/types/acquirebook";
import { toast } from "react-toastify";
import { SelectLocationModal } from "./SelectLocationModal";
import { CAutocompleteMultiFreeSoloTextField } from "../TextField/CAutocompleteMultiFreeSoloTextField";
import { preventSpaceKey } from "@/utils/utils";
import { usePictureCheckOptionsManager } from "@/hooks/usePictureCheckOptionsManager";
import { useUserConfig } from "@/hooks/useUserConfig";
import { Label } from "@/components/Label";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useReuseInfoGetMutation } from "@/hooks/useReuseInfoGetMutation";
import { AcquireBookSelectionSection } from "./AcquireBookSelectionSection";
import { ConfirmAcquireBookDialog } from "./ConfirmAcquireBookDialog";
import { useOwnerRelatedRegistries } from "@/features/acquireBook/hooks/useOwnerRelatedRegistries";
import {
  type OwnerRelatedRegistriesQuery,
  type OwnerRelatedRegistryResponse,
  type OwnerRelatedRegistrySelection,
} from "@/types/ownerRelatedRegistries";
import { OwnerRelatedRegistriesSection } from "./OwnerRelatedRegistriesSection";
import { OwnerRelatedRegistriesModal } from "./OwnerRelatedRegistriesModal";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { AllMattersAnalyzeOptionsCheckbox } from "./AllMattersAnalyzeOptionsCheckbox";

interface IPropsAcquireBookFormPage {
  acquireBookForm: AcquireBookForm;
}

/**
 * 登記一件取得UIの単体ページ版
 * @param props
 */
export const AcquireBookFormPage: React.FC<IPropsAcquireBookFormPage> = (
  props
) => {
  const { acquireBookForm } = props;
  const isReadOnly = useReadOnlyUser();

  const [searchParams] = useSearchParams();

  const {
    ownerInfoViaScraping,
    displayCheckboxAllMattersAnalyze,
    ownerRelatedSearch = true,
  } = useFeatureFlags();

  const [inputTextDirectly, setInputTextDirectly] = useState<boolean>(true);
  const [selectLocationDialogIsOpen, setSelectLocationDialogIsOpen] =
    useState<boolean>(false);

  // 確認画面のモーダルを表示するかしないかを決めるフラグ
  const [openModalFlag, setOpenModalFlag] = React.useState(false);

  // 「家屋番号検索」モーダル表示非表示管理State
  const [kaokuNumberSearchModalIsOpen, setKaokuNumberSearchModalIsOpen] =
    React.useState<boolean>(false);

  // 確認モーダルのキャンセルボタン無効化フラグ
  const [disableConfirmModalCancelButton, setDisableConfirmModalCancelButton] =
    useState<boolean>(false);

  // ページ遷移のためのナビゲーションを設定。
  const navigate = useNavigate();

  // 再利用情報取得API用のカスタムフック
  const {
    handleReuseInfoGetAPI,
    reuseInfoGetMutateAPIResponseList,
    setReuseInfoGetMutateAPIResponseList,
  } = useReuseInfoGetMutation();

  const [ownerRelatedSelections, setOwnerRelatedSelections] = useState<
    OwnerRelatedRegistrySelection[]
  >([]);
  const [ownerRelatedModalOpen, setOwnerRelatedModalOpen] =
    useState<boolean>(false);
  const [ownerRelatedQuery, setOwnerRelatedQuery] =
    useState<OwnerRelatedRegistriesQuery | null>(null);
  const [ownerRelatedQueryKey, setOwnerRelatedQueryKey] = useState<
    string | null
  >(null);
  const [ownerRelatedContext, setOwnerRelatedContext] = useState<{
    realEstateTypeLabel: string;
    prefName: string;
    cityName: string;
    locationLabel: string;
    chibanKaokuNumber: string;
  } | null>(null);
  const [ownerRelatedCachedResult, setOwnerRelatedCachedResult] = useState<{
    queryKey: string;
    rows: OwnerRelatedRegistryResponse[];
  } | null>(null);
  const ownerRelatedContextKeyRef = useRef<string | null>(null);

  const {
    search: searchOwnerRelatedRegistries,
    reset: resetOwnerRelatedRegistries,
    isLoading: isOwnerRelatedLoading,
  } = useOwnerRelatedRegistries();

  // 外部データが取得済みかどうかを判定
  const isReuseDataFetched = reuseInfoGetMutateAPIResponseList.length > 0;

  // 型を用いてReact-Hook-Formのインスタンスを作る
  const reactHookForm = useForm<AcquireBookPageFormData>({
    mode: "all",
    defaultValues: {
      prefectureId: String(
        getPrefectureIdByValue(searchParams.get("prefecture") ?? "") ?? ""
      ),
      cityCode: "",
      realEstateTypeEng: searchParams.get("realEstate") ?? "",
      locationWithoutCity: searchParams.get("location") ?? "",
      chibanKaokuNumbers: searchParams.get("chiban") // クエリパラメータは1要素のみ対応している
        ? [transformChibanKaokuNumber(searchParams.get("chiban") as string)]
        : [],
      pictureCheckOptions: getInitialPictureRequestOptions({
        realEstateTypeEng: searchParams.get("realEstate") ?? "",
        hasReuseData: isReuseDataFetched,
      }),
      identifyName: "",
      pastDays: undefined, // undefinedの場合は0になるようにCTextFieldPastDaysで変換される
      requestClosureBook: false,
      withOwnerAnalyze: false,
      withAllMattersAnalyze: false,
      allMattersAnalyzeOptions: {
        includeTitleSection: true,
        includeKoku: true,
        includeOtsuku: true,
        analyzeMethodType: "rule_based",
      },
    },
    resolver: zodResolver(createAcquireBookPageFormSchema(isReuseDataFetched)),
  });

  // 値の更新を監視
  const prefectureId = reactHookForm.watch("prefectureId");
  const cityCode = reactHookForm.watch("cityCode");
  const realEstateTypeEng = reactHookForm.watch("realEstateTypeEng");
  const locationWithoutCity = reactHookForm.watch("locationWithoutCity");
  const chibanKaokuNumbers = reactHookForm.watch("chibanKaokuNumbers");
  const pictureCheckOptions = reactHookForm.watch("pictureCheckOptions");
  const pastDays = reactHookForm.watch("pastDays");
  const requestClosureBook = reactHookForm.watch("requestClosureBook");
  const withAllMattersAnalyze = reactHookForm.watch("withAllMattersAnalyze");
  const allMattersAnalyzeOptions = reactHookForm.watch(
    "allMattersAnalyzeOptions"
  );
  const prefectureName = getPrefectureNameById(Number(prefectureId)) ?? "";

  const ownerRelatedSearchDisabled =
    realEstateTypeEng === "" ||
    prefectureId === "" ||
    cityCode === "" ||
    chibanKaokuNumbers.length !== 1 ||
    chibanKaokuNumbers[0] === "";

  const ownerRelatedHasShoyushaJiko = useMemo(
    () =>
      ownerRelatedSelections.some(
        (selection) => selection.registry.pictureType === "SHOYUSHAJIKO"
      ),
    [ownerRelatedSelections]
  );

  const ownerRelatedHasZenbujiko = useMemo(
    () =>
      ownerRelatedSelections.some(
        (selection) => selection.registry.pictureType === "ZENBUJIKO"
      ),
    [ownerRelatedSelections]
  );

  const canAnalyzeOwner =
    pictureCheckOptions.SHOYUSHAJIKO.checked || ownerRelatedHasShoyushaJiko;
  const canAnalyzeAllMatters =
    pictureCheckOptions.ZENBUJIKO.checked || ownerRelatedHasZenbujiko;

  // ユーザー設定取得
  useUserConfig({
    onUserSettingsLoaded: (data) => {
      reactHookForm.setValue("pastDays", data.pictureRequestPastDays);
    },
    // 一度でも入力があったら更新しないようにする
    enableCallbackOnRefetch: !reactHookForm.formState.isDirty,
  });

  // 閉鎖登記簿請求をチェックしたら所有者事項解析を非活性に
  useEffect(() => {
    if (requestClosureBook) {
      reactHookForm.setValue("withOwnerAnalyze", false);
    } else {
      reactHookForm.setValue("withAllMattersAnalyze", false);
    }
  }, [requestClosureBook]);

  // 市区町村セレクトボックスの内容
  const { cityList } = useCityWithExternalState(
    [prefectureId].filter((value) => value !== ""),
    searchParams.get("city") ?? "",
    (initialCityCode) => {
      if (cityCode !== initialCityCode && !reactHookForm.formState.isDirty) {
        // watchで監視しているため、現在の値と新しい値が異なる場合のみ設定
        // 手動で変更した場合は更新しない
        reactHookForm.setValue("cityCode", initialCityCode);
      }
    }
  );

  /**
   * 「city」と「市区町村以下」から所在を取得する。
   * @returns {string}
   */
  const getLocation = (
    cityCode: string,
    locationWithoutCity: string
  ): string => {
    if (inputTextDirectly) {
      const targetCity = cityList?.list.find(
        (element) => element.cityCode === cityCode
      );

      return targetCity != null
        ? targetCity.name + locationWithoutCity
        : locationWithoutCity;
    } else {
      return cityCode + locationWithoutCity;
    }
  };

  // 所在
  const location = getLocation(cityCode, locationWithoutCity);

  // 登記図面に関連するフォーム状態を管理するカスタムフック
  const {
    disabledReuseRequest,
    getReuseData,
    updatePictureCheckOptionsForm,
    getReusePictures,
  } = usePictureCheckOptionsManager({
    reactHookForm,
    initialDisabledReuseRequest: realEstateTypeEng === "",
    initialPictureCheckOptions: pictureCheckOptions,
    reuseInfoGetMutateAPIResponseList,
    setReuseInfoGetMutateAPIResponseList,
  });
  /**
   * 物件リストに物件を追加する処理。
   * 登記取得一括画面に遷移する。
   * @param {PropertySelectionRow[]} rows
   */
  const appendPropertySelectionRows = (rows: PropertySelectionRow[]): void => {
    const states = createAcquireMultipleBookHandoffData(
      realEstateTypeEng as BookTypeEng,
      chibanKaokuNumbers,
      prefectureId,
      prefectureName,
      location,
      rows
    );

    // 一括取得の画面に地番を含めて値を受け渡し、登記取得一括画面に遷移する。
    navigate("/acquiremultiplebooks", { state: states });
  };

  // 確認モーダルの取得するボタンが押された時の処理
  const handleAcquireButtonClick = async (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): Promise<void> => {
    // 一応、再度バリデーションを実行する
    await reactHookForm.handleSubmit(
      (data) => {
        const reusePictures = getReusePictures();
        if (inputTextDirectly) {
          // TODO この条件分岐は不要かもしれない
          acquireBookForm.handleSubmit(
            data.chibanKaokuNumbers,
            data.prefectureId,
            data.cityCode,
            data.locationWithoutCity,
            "",
            data.pastDays,
            data.realEstateTypeEng,
            data.pictureCheckOptions,
            data.withOwnerAnalyze,
            data.withAllMattersAnalyze,
            data.identifyName,
            cancelLoading,
            setDisableConfirmModalCancelButton,
            reusePictures,
            data.requestClosureBook,
            ownerRelatedSelections,
            data.allMattersAnalyzeOptions
          );
        } else {
          acquireBookForm.handleSubmit(
            data.chibanKaokuNumbers,
            prefectureId,
            "",
            "",
            getLocation(data.cityCode, data.locationWithoutCity),
            data.pastDays,
            data.realEstateTypeEng,
            data.pictureCheckOptions,
            data.withOwnerAnalyze,
            data.withAllMattersAnalyze,
            data.identifyName,
            cancelLoading,
            setDisableConfirmModalCancelButton,
            reusePictures,
            data.requestClosureBook,
            ownerRelatedSelections,
            data.allMattersAnalyzeOptions
          );
        }
      },
      () => {
        cancelLoading();
      }
    )();
  };

  // 再利用情報取得ボタンが押された時の処理
  const handleReuseInfoGetClick = async (
    cancelLoading: () => void
  ): Promise<void> => {
    const newReuseInfoGetMutateAPIResponseList = await handleReuseInfoGetAPI(
      {
        real_estate_type: realEstateTypeEng,
        pref_code: prefectureId,
        city_code: cityCode,
        location_without_city: locationWithoutCity,
      },
      chibanKaokuNumbers,
      cancelLoading
    );
    updatePictureCheckOptionsForm(
      realEstateTypeEng,
      prefectureId,
      location,
      chibanKaokuNumbers,
      newReuseInfoGetMutateAPIResponseList.length === 0, // 再利用情報がない場合は必ず非表示
      true // チェック状態を保持する
    );
  };

  const serializeOwnerRelatedQuery = useCallback(
    (query: OwnerRelatedRegistriesQuery): string => {
      return JSON.stringify({
        realEstateType: query.realEstateType,
        prefCode: query.prefCode,
        cityCode: query.cityCode ?? "",
        locationFull: query.locationFull ?? "",
        locationWithoutCity: query.locationWithoutCity ?? "",
        chibanKaokuNumber: query.chibanKaokuNumber,
        pastDays: query.pastDays ?? "",
      });
    },
    []
  );

  const handleOwnerRelatedSearchClick = (): void => {
    if (ownerRelatedSearchDisabled) {
      return;
    }

    const baseQuery: OwnerRelatedRegistriesQuery = {
      realEstateType: realEstateTypeEng,
      prefCode: prefectureId,
      chibanKaokuNumber: chibanKaokuNumbers[0],
    };

    if (inputTextDirectly) {
      const locationFull = getLocation(cityCode, locationWithoutCity);
      if (locationFull) {
        baseQuery.locationFull = locationFull;
      }
    } else {
      if (cityCode !== "") {
        baseQuery.cityCode = cityCode;
      }
      if (locationWithoutCity !== "") {
        baseQuery.locationWithoutCity = locationWithoutCity;
      }
    }

    if (typeof pastDays === "number") {
      baseQuery.pastDays = pastDays;
    }

    setOwnerRelatedQuery(baseQuery);
    setOwnerRelatedQueryKey(serializeOwnerRelatedQuery(baseQuery));

    const cityName =
      cityList?.list.find((city) => city.cityCode === cityCode)?.name ?? "";
    const realEstateTypeLabel =
      realEstateTypeEng !== ""
        ? convertBookTypeToJpn(realEstateTypeEng as BookTypeEng)
        : "";

    setOwnerRelatedContext({
      realEstateTypeLabel,
      prefName: prefectureName,
      cityName,
      locationLabel: locationWithoutCity,
      chibanKaokuNumber: chibanKaokuNumbers[0],
    });

    setOwnerRelatedModalOpen(true);
  };

  const resetOwnerRelatedData = useCallback(() => {
    setOwnerRelatedSelections([]);
    setOwnerRelatedCachedResult(null);
    setOwnerRelatedQuery(null);
    setOwnerRelatedQueryKey(null);
    resetOwnerRelatedRegistries();
  }, [resetOwnerRelatedRegistries]);

  const handleCloseOwnerRelatedModal = (): void => {
    setOwnerRelatedModalOpen(false);
    setOwnerRelatedQuery(null);
    setOwnerRelatedQueryKey(null);
  };

  // フィーチャーフラグがOFFの場合は関連データをクリアしてUIを閉じる
  useEffect(() => {
    if (!ownerRelatedSearch) {
      resetOwnerRelatedData();
      setOwnerRelatedModalOpen(false);
    }
  }, [ownerRelatedSearch, resetOwnerRelatedData]);

  useEffect(() => {
    const disabled = ownerRelatedSearchDisabled;
    const key = disabled
      ? null
      : JSON.stringify({
          prefectureId,
          cityCode,
          realEstateTypeEng,
          locationWithoutCity,
          chibanKaokuNumbers: chibanKaokuNumbers.join("|"),
        });

    const shouldClear =
      disabled ||
      (!!ownerRelatedContextKeyRef.current &&
        ownerRelatedContextKeyRef.current !== key);

    if (shouldClear) {
      if (
        ownerRelatedSelections.length > 0 ||
        ownerRelatedCachedResult !== null
      ) {
        toast.warning(
          "所在や地番が変わったため、同一オーナー名検索の選択状態をリセットしました。"
        );
      }
      resetOwnerRelatedData();
      ownerRelatedContextKeyRef.current = key;
      setOwnerRelatedModalOpen(false);
      return;
    }

    ownerRelatedContextKeyRef.current = key;
  }, [
    ownerRelatedSearchDisabled,
    prefectureId,
    cityCode,
    realEstateTypeEng,
    locationWithoutCity,
    chibanKaokuNumbers,
    ownerRelatedSelections.length,
    ownerRelatedCachedResult,
    resetOwnerRelatedData,
  ]);

  return (
    <>
      {/* 確認のモーダル画面 */}
      <ConfirmAcquireBookDialog
        open={openModalFlag}
        setOpen={setOpenModalFlag}
        pictureCheckOptions={pictureCheckOptions}
        handleAcquireButtonClick={handleAcquireButtonClick}
        disableConfirmModalCancelButton={disableConfirmModalCancelButton}
        isReadOnly={isReadOnly}
      />

      {/* 家屋番号検索モーダル */}
      <Modal
        open={kaokuNumberSearchModalIsOpen}
        aria-labelledby={"kaoku-number-search-modal"}
        aria-describedby={"kaoku-number-search-modal"}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            outline: "none",
          }}
        >
          <Box
            sx={{
              width: 1600,
              maxWidth: "90%",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <ChibanKaokuNumberSearchModal
              variant={"book"}
              chibanList={chibanKaokuNumbers.map((chibanKaokuNumber) => ({
                prefectureName,
                location,
                chiban: chibanKaokuNumber,
                bookType: realEstateTypeEng as BookTypeEng,
              }))}
              setKaokuNumberSearchModalIsOpen={setKaokuNumberSearchModalIsOpen}
              appendPropertySelectionRows={appendPropertySelectionRows}
            />
          </Box>
        </Box>
      </Modal>

      {/* 所在選択ダイアログ */}
      <SelectLocationModal
        open={selectLocationDialogIsOpen}
        onClose={() => {
          setSelectLocationDialogIsOpen(false);
        }}
        prefectureId={Number(prefectureId)}
        cityInfo={{
          setCode: (cityCode: string) => {
            reactHookForm.setValue("cityCode", cityCode);
            void reactHookForm.trigger("cityCode"); // バリデーションを明示的に再実行
          },
          listResponse: cityList ?? { list: [] },
        }}
        setLocationWithoutCity={(locationWithoutCity: string) => {
          reactHookForm.setValue("locationWithoutCity", locationWithoutCity);
          void reactHookForm.trigger("locationWithoutCity"); // バリデーションを明示的に再実行
        }}
        setInputTextDirectly={setInputTextDirectly}
      />

      <>
        <Box sx={{ m: 2 }}>
          <Grid container spacing={2} justifyContent={"left"} sx={{ my: 0 }}>
            <Grid item>
              <Box>
                <Label required={true}>不動産種別</Label>
                <CSelect
                  control={reactHookForm.control}
                  name="realEstateTypeEng"
                  label="不動産種別"
                  options={getRealEstateTypeEngSelectObject()}
                  isClearable={false}
                  callbackOnChange={(newRealEstateTypeEng) => {
                    updatePictureCheckOptionsForm(
                      newRealEstateTypeEng,
                      prefectureId,
                      location,
                      chibanKaokuNumbers,
                      true, // ラジオボタン非表示
                      true // チェック状態を保持する
                    );
                  }}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            justifyContent={"left"}
            alignItems={"center"}
            mt={0}
          >
            <Grid item>
              <Box>
                <Label required={true}>都道府県</Label>
                <CSelect
                  control={reactHookForm.control}
                  name="prefectureId"
                  label="都道府県"
                  options={getPrefectureSelectObject()}
                  callbackOnChange={(newPrefectureId) => {
                    // 市区町村selectBoxの中身が変わるのでselectBoxを初期状態に戻す
                    reactHookForm.setValue("cityCode", "");
                    reactHookForm.setValue("locationWithoutCity", "");
                    updatePictureCheckOptionsForm(
                      realEstateTypeEng,
                      newPrefectureId,
                      location,
                      chibanKaokuNumbers,
                      true, // ラジオボタン非表示
                      true // チェック状態を保持する
                    );
                  }}
                  isClearable={false}
                />
              </Box>
            </Grid>
            <Grid item display="flex" mt={3} ml={3}>
              <Button
                label="所在選択"
                onClick={() => {
                  if (prefectureId === "") {
                    // 都道府県が選択されていない場合
                    toast.info("都道府県を選択してください");
                  } else {
                    setSelectLocationDialogIsOpen(true);
                  }
                }}
                size={SizingWrapperStyle.SMALL}
                variant={ButtonVariantOption.Outlined}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            justifyContent={"left"}
            alignItems={"center"}
            mt={0}
          >
            <Grid item>
              <Box>
                <Label required={true}>市区町村</Label>
                <CSelect
                  control={reactHookForm.control}
                  name="cityCode"
                  label="市区町村"
                  options={getCitySelectObject(cityList ?? { list: [] })}
                  callbackOnChange={(newCityCode) => {
                    setInputTextDirectly(true);
                    updatePictureCheckOptionsForm(
                      realEstateTypeEng,
                      prefectureId,
                      getLocation(newCityCode, locationWithoutCity),
                      chibanKaokuNumbers,
                      true, // ラジオボタン非表示
                      true // チェック状態を保持する
                    );
                  }}
                  isClearable={false}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            justifyContent={"left"}
            alignItems={"flex-start"} // 地番または家屋番号が複数行になる場合があるため、動かないように上寄せにする
            mt={0}
          >
            <Grid item>
              <Label>大字・町丁目</Label>
              <Box display="flex" sx={{ width: "500px" }}>
                <CTextField
                  control={reactHookForm.control}
                  name="locationWithoutCity"
                  label="大字・町丁目"
                  size={SizingWrapperStyle.INHERIT}
                  callbackOnChange={(event) => {
                    updatePictureCheckOptionsForm(
                      realEstateTypeEng,
                      prefectureId,
                      getLocation(cityCode, event.target.value),
                      chibanKaokuNumbers,
                      true, // ラジオボタン非表示
                      true // チェック状態を保持する
                    );
                  }}
                />
              </Box>
            </Grid>
            <Grid item>
              <Label required={true}>地番または家屋番号</Label>
              <Box display="flex" sx={{ width: "500px" }}>
                <CAutocompleteMultiFreeSoloTextField
                  control={reactHookForm.control}
                  name="chibanKaokuNumbers"
                  label="地番または家屋番号"
                  size={SizingWrapperStyle.INHERIT}
                  onKeyDown={preventSpaceKey}
                  handleTransformValue={transformChibanKaokuNumber}
                  callbackOnChange={(newValues) => {
                    updatePictureCheckOptionsForm(
                      realEstateTypeEng,
                      prefectureId,
                      location,
                      newValues,
                      true, // ラジオボタン非表示
                      true // チェック状態を保持する
                    );
                    if (newValues.length > 1) {
                      // 地番または家屋番号が複数選択の場合は、不必要なバリデーションはクリア
                      reactHookForm.clearErrors(["pictureCheckOptions"]);
                    }
                  }}
                />
              </Box>
            </Grid>
            <Grid item>
              <Box sx={{ mt: 4 }}>
                <Button
                  disabled={checkKaokuNumberSearchDisabled({
                    realEstateTypeEng,
                    prefectureId,
                    location,
                    chibanKaokuNumbers,
                  })}
                  label="地番・家屋番号検索"
                  onClick={() => {
                    setKaokuNumberSearchModalIsOpen(true);
                  }}
                  size={SizingWrapperStyle.MEDIUM}
                  variant={ButtonVariantOption.Outlined}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            justifyContent={"left"}
            alignItems={"center"}
            mt={1}
          >
            <Grid item>
              <Label>識別名</Label>
              <Box sx={{ width: "500px" }}>
                <CTextFieldIdentifyName
                  name={"identifyName"}
                  control={reactHookForm.control}
                  size={SizingWrapperStyle.INHERIT}
                />
              </Box>
            </Grid>
            <Grid item>
              <Label required={true}>経過日数</Label>
              <Box>
                <CTextFieldPastDays
                  name="pastDays"
                  control={reactHookForm.control}
                  tooltipText={pastDaysDescription}
                  size={SizingWrapperStyle.LARGE}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            justifyContent={"left"}
            alignItems={"center"}
            mt={1}
          >
            <Grid item>
              <Label>閉鎖登記簿</Label>
              <Box ml={2}>
                <CCheckbox
                  name="requestClosureBook"
                  label="閉鎖登記簿請求"
                  control={reactHookForm.control}
                />
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 3 }}>
            <FormProvider {...reactHookForm}>
              <AcquireBookSelectionSection
                disabled={chibanKaokuNumbers.length > 1}
                isDataFetched={isReuseDataFetched}
                onClickReuseInfoGetButton={(_event, cancelLoading) => {
                  void handleReuseInfoGetClick(cancelLoading);
                }}
                getReuseData={getReuseData}
                disabledReuseRequestButton={disabledReuseRequest}
                handleCustomOptionChange={(event, key) => {
                  // NOTE: ページとモーダルで共通の処理はここに書かない
                  if (key === "SHOYUSHAJIKO" && !event) {
                    reactHookForm.setValue("withOwnerAnalyze", false);
                  }
                  if (key === "ZENBUJIKO" && !event) {
                    reactHookForm.setValue("withAllMattersAnalyze", false);
                  }
                }}
              />
            </FormProvider>
          </Box>
        </Box>
        {ownerRelatedSearch && (
          <OwnerRelatedRegistriesSection
            disabled={ownerRelatedSearchDisabled}
            isSearching={isOwnerRelatedLoading}
            selectionCount={ownerRelatedSelections.length}
            onSearchClick={handleOwnerRelatedSearchClick}
          />
        )}
        <Box
          sx={
            ownerInfoViaScraping
              ? {
                  mx: 3,
                  mt: 1,
                  mb: 3,
                  display: "flex",
                  justifyContent: "space-between",
                }
              : {
                  mx: 2,
                  mt: 2,
                  mb: 3,
                  display: "flex",
                  justifyContent: "center",
                }
          }
        >
          {(ownerInfoViaScraping || displayCheckboxAllMattersAnalyze) && (
            <Box display={"flex"} flexDirection="column" gap={1}>
              {ownerInfoViaScraping && (
                <CCheckbox
                  control={reactHookForm.control}
                  name="withOwnerAnalyze"
                  label="所有者事項の取得と同時に内容を解析する"
                  disabled={!canAnalyzeOwner}
                />
              )}
              {displayCheckboxAllMattersAnalyze && (
                <>
                  <CCheckbox
                    control={reactHookForm.control}
                    name="withAllMattersAnalyze"
                    label="全部事項の取得と同時に内容を解析する"
                    disabled={!canAnalyzeAllMatters}
                  />
                  {withAllMattersAnalyze && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <AllMattersAnalyzeOptionsCheckbox
                        disabled={!canAnalyzeAllMatters || requestClosureBook}
                        value={allMattersAnalyzeOptions}
                        onChange={(value) => {
                          reactHookForm.setValue(
                            "allMattersAnalyzeOptions",
                            value,
                            {
                              shouldValidate: true,
                            }
                          );
                        }}
                      />
                      {reactHookForm.formState.errors.allMattersAnalyzeOptions
                        ?.message != null && (
                        <FormHelperText error sx={{ textAlign: "center" }}>
                          {
                            reactHookForm.formState.errors
                              .allMattersAnalyzeOptions?.message
                          }
                        </FormHelperText>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
          {chibanKaokuNumbers.length > 1 ? (
            <Button
              label="不動産登記簿の一括取得に進む"
              onClick={() => {
                void (async () => {
                  // 必要な項目のバリデーションを実行する
                  const isValid = await reactHookForm.trigger([
                    "realEstateTypeEng",
                    "prefectureId",
                    "cityCode",
                    "locationWithoutCity",
                    "pastDays",
                  ]);

                  if (isValid) {
                    // バリデーション成功時の処理
                    const identifyName =
                      reactHookForm.getValues("identifyName");
                    const data = createAcquireMultipleBookHandoffData(
                      realEstateTypeEng as BookTypeEng,
                      chibanKaokuNumbers.reverse(), // 入力順と逆にする
                      prefectureId,
                      prefectureName,
                      location,
                      []
                    );

                    const stateData: AcquireMultipleBookLocationState = {
                      data,
                      identifyName,
                      pastDays,
                    };

                    // 一括取得の画面に地番を含めて値を受け渡し、登記取得一括画面に遷移する。
                    navigate("/acquiremultiplebooks", {
                      state: stateData,
                    });
                  }
                })();
              }}
              variant={ButtonVariantOption.Contained}
              size={SizingWrapperStyle.MEDIUM}
            />
          ) : (
            <Button
              label="取得する"
              onClick={() => {
                void (async () => {
                  const isValid = await reactHookForm.trigger();
                  if (isValid) {
                    setOpenModalFlag(true);
                  }
                })();
              }}
              variant={ButtonVariantOption.Contained}
              size={SizingWrapperStyle.SMALL}
            />
          )}
        </Box>
      </>
      {ownerRelatedSearch && ownerRelatedContext && (
        <OwnerRelatedRegistriesModal
          open={ownerRelatedModalOpen}
          onClose={handleCloseOwnerRelatedModal}
          context={ownerRelatedContext}
          query={ownerRelatedQuery}
          queryKey={ownerRelatedQueryKey}
          initialSelection={ownerRelatedSelections}
          search={searchOwnerRelatedRegistries}
          reset={resetOwnerRelatedRegistries}
          isLoading={isOwnerRelatedLoading}
          cachedResult={
            ownerRelatedQueryKey != null &&
            ownerRelatedCachedResult?.queryKey === ownerRelatedQueryKey
              ? ownerRelatedCachedResult
              : null
          }
          onSearchCompleted={(query, rows) => {
            setOwnerRelatedCachedResult({
              queryKey: serializeOwnerRelatedQuery(query),
              rows,
            });
          }}
          onSubmit={(selection) => {
            setOwnerRelatedSelections(selection);
          }}
        />
      )}
    </>
  );
};
